import { useCallback, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import MobileSelectDates from '../Common/MobileSelectDates'

import ModalBase from 'LuxKit/Modal/ModalBase'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useUpdateTrip, useTrip } from 'tripPlanner/hooks/api/trip'
import * as Mapper from 'tripPlanner/mappers'

interface Props {
  tripId: string
}

/** Just like the version in EditTripDatesTP, except this one is for use with ModalContext */
function EditTripDates(props: Props) {
  const { tripId } = props

  const { data: trip } = useTrip({ tripId })

  const { startDate, endDate } = trip ?? {}

  const initialHasDates = useRef(!!(startDate && endDate)).current

  const modalElement = useModalElementContext<{
    startDate: moment.Moment | undefined
    endDate: moment.Moment | undefined
  }>()
  const intl = useIntl()

  const datesRemovedMessage = intl.formatMessage({
    id: '0239IncxnHK5a54O_T-q',
    defaultMessage: 'Dates removed',
    description: 'Snackbar message after removing dates from a trip in the trip planner item editor modal',
  })

  const datesSavedMessage = intl.formatMessage({
    id: 'lktGJK-ZViHdxtEYbzHr',
    defaultMessage: 'Dates saved',
    description: 'Snackbar message after saving dates to a trip in the trip planner item editor modal',
  })

  const datesErrorMessage = intl.formatMessage({
    id: 'rnJ0XHvmD9YL4vgJcENi',
    defaultMessage: 'Error while updating trip dates. Please try again.',
    description: 'Error message when trip date update fails in the trip planner',
  })

  const editDatesTitle = intl.formatMessage({
    id: 's226en44H3yoT9gvjaZZ',
    defaultMessage: 'Edit dates',
    description: 'Modal title when editing existing trip dates in the trip planner',
  })

  const addDatesTitle = intl.formatMessage({
    id: 'xbjj8-MHo8XVhRGJgfwg',
    defaultMessage: 'Add dates',
    description: 'Modal title when adding dates to a trip for the first time',
  })

  const saveLabel = intl.formatMessage({
    id: 'oxw_v_es-3-oC1_qM4zj',
    defaultMessage: 'Save',
    description: 'Save button label in the trip dates modal in the trip planner',
  })

  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const { mutate, isLoading } = useUpdateTrip({
    onMutate: (): undefined => {
      setErrorMsg(undefined)
    },
    onSuccess: (updatedTripRaw) => {
      const trip = Mapper.fullTrip(updatedTripRaw)
      modalElement.resolve({ startDate: trip.startDate, endDate: trip.endDate })
      const hasDates = !!(trip.startDate && trip.endDate)
      if (initialHasDates && !hasDates) {
        showSnackbar(datesRemovedMessage, 'success')
      } else if (hasDates) {
        showSnackbar(datesSavedMessage, 'success')
      }
    },
    onError: () => {
      setErrorMsg(datesErrorMessage)
    },
  })

  const updateTripDates = useCallback(
    (startDate: moment.Moment, endDate?: moment.Moment) => {
      mutate({
        tripId,
        startDate,
        endDate: endDate ?? startDate,
      })
    },
    [mutate, tripId],
  )

  const clearTripDates = useCallback(() => {
    mutate({
      tripId,
      startDate: null,
      endDate: null,
    })
  }, [mutate, tripId])

  return (
    <ModalBase>
      <MobileSelectDates
        initialStartDate={startDate}
        initialEndDate={endDate}
        onChange={updateTripDates}
        onRemove={startDate ? clearTripDates : undefined}
        onCancel={() =>
          modalElement.resolve({
            startDate: trip?.startDate,
            endDate: trip?.endDate,
          })}
        title={startDate ? editDatesTitle : addDatesTitle}
        doneLabel={startDate ? saveLabel : addDatesTitle}
        isSubmitting={isLoading}
        errorMsg={errorMsg}
      />
    </ModalBase>
  )
}

export default EditTripDates
